import * as React from 'react'
import { useRef, useState } from 'react'

import { useBalanceOperationsWidget } from './hooks'
import { BalanceOperation } from './types'
import { path } from '../../../../libs'
import { ColumnsType } from 'antd/lib/table'
import { Button, Input, InputRef, Space, Table } from 'antd'
import { toPath } from '../../../../features/router'
import { FilterConfirmProps } from 'antd/es/table/interface'
import {
  CheckCircleOutlined,
  CloseSquareOutlined,
  SearchOutlined,
} from '@ant-design/icons'
import Highlighter from 'react-highlight-words'

type DataIndex = keyof BalanceOperation

export const BalanceOperationsWidget = () => {
  const { operations, loading } = useBalanceOperationsWidget()

  return (
    <div>
      <AccountsList items={operations} loading={loading} />
    </div>
  )
}

type BalanceOperationsListProps = {
  items: BalanceOperation[] | undefined
  loading: boolean
}

const AccountsList = ({ items, loading }: BalanceOperationsListProps) => {
  const [activeKey, setActiveKey] = useState(1)

  return (
    <div>
      <BalanceOperationsTable
        items={items}
        loading={loading}
      ></BalanceOperationsTable>
    </div>
  )
}

const BalanceOperationsTable = ({
  items,
  loading,
}: BalanceOperationsListProps) => {
  const [searchText, setSearchText] = useState('')
  const [searchedColumn, setSearchedColumn] = useState('')
  const searchInput = useRef<InputRef>(null)

  const handleSearch = (
    selectedKeys: string[],
    confirm: (param?: FilterConfirmProps) => void,
    dataIndex: DataIndex,
  ) => {
    confirm()
    setSearchText(selectedKeys[0])
    setSearchedColumn(dataIndex)
  }

  const handleReset = (clearFilters: () => void) => {
    clearFilters()
    setSearchText('')
  }

  const getColumnSearchProps = (
    dataIndex: DataIndex,
  ): {
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
      close,
    }: {
      setSelectedKeys: any
      selectedKeys: any
      confirm: any
      clearFilters?: any
      close: any
    }) => JSX.Element
    filterIcon: (filtered: boolean) => JSX.Element
    onFilter: (value: any, record: any) => boolean
    render: (text: any) => any
    onFilterDropdownOpenChange: (visible: any) => void
  } => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
      close,
    }) => (
      <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
        <Input
          ref={searchInput}
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() =>
            handleSearch(selectedKeys as string[], confirm, dataIndex)
          }
          style={{ marginBottom: 8, display: 'block' }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() =>
              handleSearch(selectedKeys as string[], confirm, dataIndex)
            }
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            Search
          </Button>
          <Button
            onClick={() => clearFilters && handleReset(clearFilters)}
            size="small"
            style={{ width: 90 }}
          >
            Reset
          </Button>
          <Button
            type="link"
            size="small"
            onClick={() => {
              close()
            }}
          >
            close
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered: boolean) => (
      <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />
    ),
    onFilter: (value, record) =>
      record[dataIndex]
        .toString()
        .toLowerCase()
        .includes((value as string).toLowerCase()),
    onFilterDropdownOpenChange: (visible) => {
      if (visible) {
        setTimeout(() => searchInput.current?.select(), 100)
      }
    },
    render: (text) =>
      searchedColumn === dataIndex ? (
        <Highlighter
          highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
          searchWords={[searchText]}
          autoEscape
          textToHighlight={text ? text.toString() : ''}
        />
      ) : (
        text
      ),
  })

  const columns: ColumnsType<BalanceOperation> = [
    {
      title: 'ID',
      dataIndex: 'operationId',
      ...getColumnSearchProps('operationId'),
      defaultSortOrder: 'descend',
      sorter: (a, b) => parseInt(a.operationId) - parseInt(b.operationId),
    },
    {
      title: 'Operation type',
      dataIndex: 'operationType',
      ...getColumnSearchProps('operationType'),
    },
    {
      title: 'paymentMethod',
      dataIndex: 'paymentMethod',
      ...getColumnSearchProps('paymentMethod'),
    },
    {
      title: 'sourceAmount',
      dataIndex: 'sourceAmount',
      ...getColumnSearchProps('sourceAmount'),
    },
    {
      title: 'createdAt',
      dataIndex: 'createdAt',
      ...getColumnSearchProps('createdAt'),
    },
    {
      title: 'applyAt',
      dataIndex: 'applyAt',
      ...getColumnSearchProps('applyAt'),
    },
    { title: 'Comment', dataIndex: 'comment' },
  ]

  return (
    <div>
      <Table
        dataSource={items}
        columns={columns}
        bordered={true}
        scroll={{ x: true }}
        loading={loading}
        rowKey="operationId"
      ></Table>
    </div>
  )
}
